/**
 * The disk half of the catalog cache — a hand-rolled IndexedDB key/value store.
 *
 * Why IndexedDB and not `localStorage`: the demo tenant's `/catalog` is ~100 KB
 * of JSON for 167 items and a real venue is several times that. `localStorage`
 * is synchronous, so every read blocks the main thread at exactly the moment the
 * till screen is trying to paint, and its quota (~5 MB, shared with the tokens
 * and the locale) is not a budget a menu can live inside. `@/lib/offline`'s
 * `OfflineStorage` port already says the same thing for the order outbox.
 *
 * Why no dependency: `idb` would be a reasonable 2 KB, but everything this cache
 * needs is one object store, `get`, `put`, `getAll` and `delete`. Sixty lines of
 * request-to-promise plumbing is less code than the wrapper's own surface, and
 * it lets every failure path degrade to "no cache" instead of throwing — which
 * is the behaviour that matters. A browser in private mode, a blocked upgrade,
 * a quota refusal: none of them may take the till down. Every function here
 * resolves; none of them reject.
 *
 * Records are scoped by tenant id, taken from the access token's `tid` claim
 * rather than from Redux, because hydration happens before `fetchMe` has
 * answered and a menu shown under the wrong tenant is worse than no menu.
 */

const DB_NAME = 'tarabeeza-catalog';
const DB_VERSION = 1;
const STORE = 'queries';
const TOKEN_KEY = 'venue.tokens';

/**
 * How old a saved menu may be and still be worth painting.
 *
 * A cached price is display-only either way (§38 — the server prices what it
 * charges), so age does not risk money. What it risks is a grid full of items
 * the venue has since deleted, and past a week the operator is better served by
 * the spinner than by fiction.
 */
export const CATALOG_CACHE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

/** Beyond this the oldest records are dropped — a runaway arg permutation must
 *  not grow the store without bound. */
const MAX_RECORDS = 24;

export interface CachedQueryRecord<T = unknown> {
  /** `${tenantId}|${endpointName}|${stableArgs}` */
  key: string;
  tenantId: string;
  endpointName: string;
  savedAt: number;
  payload: T;
}

let dbPromise: Promise<IDBDatabase | null> | null = null;

function openDb(): Promise<IDBDatabase | null> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise<IDBDatabase | null>((resolve) => {
    if (typeof indexedDB === 'undefined') {
      resolve(null);
      return;
    }
    let request: IDBOpenDBRequest;
    try {
      request = indexedDB.open(DB_NAME, DB_VERSION);
    } catch {
      resolve(null);
      return;
    }
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'key' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => resolve(null);
    // An older tab holding version 1 open would block an upgrade forever. Give
    // up rather than hang the caller — a missing cache is a slow screen, a
    // pending promise is a screen that never renders.
    request.onblocked = () => resolve(null);
  });
  return dbPromise;
}

/**
 * Runs one request inside a transaction and resolves when the transaction
 * *completes*, not when the request succeeds — a write that is only "requested"
 * is not on disk yet, and the point of this store is surviving a reload.
 */
function run<T>(
  mode: IDBTransactionMode,
  work: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T | null> {
  return openDb().then(
    (db) =>
      new Promise<T | null>((resolve) => {
        if (!db) {
          resolve(null);
          return;
        }
        let result: T | null = null;
        try {
          const tx = db.transaction(STORE, mode);
          const request = work(tx.objectStore(STORE));
          request.onsuccess = () => {
            result = request.result;
          };
          tx.oncomplete = () => resolve(result);
          tx.onerror = () => resolve(null);
          tx.onabort = () => resolve(null);
        } catch {
          resolve(null);
        }
      }),
  );
}

/**
 * The tenant these records belong to, read from the JWT rather than the store.
 *
 * The dashboard stops sending `X-Tenant-Id` once it is authenticated — the
 * tenant lives in the token's `tid` claim — so this is the same identity the
 * API will scope the response by, and it is readable synchronously at start-up.
 */
export function cacheTenantId(): string | null {
  try {
    const raw = localStorage.getItem(TOKEN_KEY);
    if (!raw) return null;
    const accessToken = (JSON.parse(raw) as { accessToken?: string }).accessToken;
    const body = accessToken?.split('.')[1];
    if (!body) return null;
    const claims = JSON.parse(atob(body.replace(/-/g, '+').replace(/_/g, '/'))) as {
      tid?: unknown;
    };
    return typeof claims.tid === 'string' ? claims.tid : null;
  } catch {
    return null;
  }
}

/** Key-order-independent identity for an argument object, so `{a,b}` and `{b,a}`
 *  do not each get their own copy of the menu on disk. */
export function stableArgs(args: unknown): string {
  if (args === null || typeof args !== 'object') return JSON.stringify(args ?? null);
  const entries = Object.entries(args as Record<string, unknown>)
    .filter(([, value]) => value !== undefined)
    .sort(([a], [b]) => (a < b ? -1 : 1));
  return JSON.stringify(entries);
}

export function cacheKey(tenantId: string, endpointName: string, args: unknown): string {
  return `${tenantId}|${endpointName}|${stableArgs(args)}`;
}

/** The saved payload for a key, or null — never a throw, never a stale record. */
export async function readCachedQuery<T>(key: string): Promise<CachedQueryRecord<T> | null> {
  const record = await run<CachedQueryRecord<T> | undefined>('readonly', (store) =>
    store.get(key) as IDBRequest<CachedQueryRecord<T> | undefined>,
  );
  if (!record) return null;
  if (Date.now() - record.savedAt > CATALOG_CACHE_MAX_AGE_MS) return null;
  return record;
}

export async function writeCachedQuery<T>(
  key: string,
  tenantId: string,
  endpointName: string,
  payload: T,
): Promise<void> {
  const record: CachedQueryRecord<T> = {
    key,
    tenantId,
    endpointName,
    savedAt: Date.now(),
    payload,
  };
  await run('readwrite', (store) => store.put(record));
}

/**
 * Drops everything that is expired or belongs to another tenant.
 *
 * Called once when the slice loads, alongside — not instead of — the full wipe
 * `authSlice`'s logout thunk does. Tenant-scoped keys already make another
 * tenant's menu unreadable, so this is housekeeping rather than the isolation
 * boundary.
 */
export async function pruneCatalogCache(tenantId: string | null): Promise<void> {
  const all = await run<CachedQueryRecord[]>('readonly', (store) => store.getAll());
  if (!all?.length) return;

  const now = Date.now();
  const doomed = all.filter(
    (record) => record.tenantId !== tenantId || now - record.savedAt > CATALOG_CACHE_MAX_AGE_MS,
  );

  const survivors = all
    .filter((record) => !doomed.includes(record))
    .sort((a, b) => b.savedAt - a.savedAt);
  doomed.push(...survivors.slice(MAX_RECORDS));

  for (const record of doomed) {
    await run('readwrite', (store) => store.delete(record.key));
  }
}

/** Wipes the store. Called on sign-out, so one operator's menu does not
 *  greet the next one on a shared terminal. */
export async function clearCatalogCache(): Promise<void> {
  await run('readwrite', (store) => store.clear());
}
